import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import { CreationButtons } from '../../../../../../shared/components/ui/creation-buttons/creation-buttons';
import { LabelComponent } from '../../../../../../shared/components/form/label/label.component';
import { ErrorService } from '../../../../../../shared/services/error.service';
import { CombosService } from '../../../../../../shared/services/combos.service';
import { GenericCombo } from '../../../../../../shared/services/interfaces/combos.interface';
import { SnackbarService } from '@shared/services/snackbar.service';
import { ContactoAdministrativoPayload } from '../contactos-administrativos.interface';
import { ContactosAdministrativosService } from '../contactos-administrativos.service';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { SwitchComponent } from '@shared/components/form/switch/switch.component';
import { AppFormComponent } from '@shared/components/ui/list-components';

@Component({
  selector: 'app-crear-contacto-administrativo',
  imports: [HasPermissionDirective, CreationButtons, LabelComponent, ReactiveFormsModule, SelectFieldComponent, InputFieldComponent, SwitchComponent, AppFormComponent],
  templateUrl: './crear-contacto-administrativo.html',
})
export class CrearContactoAdministrativo implements OnInit {
  saveForm!: FormGroup;
  loading = false;
  tiposContactosAdministrativos: GenericCombo[] = [];
  establecimientos: GenericCombo[] = [];

  get tiposContactosAdministrativosOptions(): SelectOption[] { return this.tiposContactosAdministrativos.map(x => ({ value: x.id, label: x.nombre })); }
  get establecimientosOptions(): SelectOption[] { return this.establecimientos.map(x => ({ value: x.id, label: x.nombre })); }

  private fB = inject(FormBuilder);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private contactosAdministrativosService = inject(ContactosAdministrativosService);
  private dialogRef = inject(MatDialogRef<CrearContactoAdministrativo>);
  private cliClienteId = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.getCombos();
    this.makeForm();
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      cli_cliente_id: [this.cliClienteId, Validators.required],
      // Vacío = contacto administrativo del cliente; con valor = contacto de ese establecimiento
      cli_establecimiento_id: [''],
      param_establecimiento_tipo_contacto_id: ['', Validators.required],
      nombre: ['', Validators.required],
      apellido: ['', Validators.required],
      celular: ['', Validators.required],
      email: ['', Validators.email],
      activo: [true],
    });
  }

  private getCombos(): void {
    // Se usa el combo completo (no solo los tipos administrativos) porque desde
    // acá se dan de alta tanto contactos administrativos como de establecimiento.
    this.combosService
      .getEstablecimientosTiposContactosCombo()
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.tiposContactosAdministrativos = response.data;
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      });

    this.combosService
      .getEstablecimientosCombo(this.cliClienteId)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.establecimientos = response.data;
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      });
  }

  create(): void {
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formValue = this.saveForm.value;
    const payload: ContactoAdministrativoPayload = {
      ...formValue,
      cli_establecimiento_id: formValue.cli_establecimiento_id || null,
      email: formValue.email || null,
      activo: !!formValue.activo
    };

    this.contactosAdministrativosService
      .store(payload)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.snackbarService.showSnackBar('Contacto creado correctamente');
            this.dialogRef.close(true);
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      })
      .add(() => {
        this.loading = false;
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
